import React, { useEffect } from 'react';
import { Card, Spin } from 'antd';
import type {
  EmailConfigurationCreate,
  EmailConfigurationUpdate,
} from '../types/emailConfiguration.types';
import { useEmailConfigurationForm } from '../hooks/use-emailConfiguration-form.hook';

import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Input, InputNumber, Switch } from 'antd';

interface EmailConfigurationFormProps {
  defaultValues?: DefaultValues<EmailConfigurationCreate | EmailConfigurationUpdate>;
  onFormSubmit: (
    data: EmailConfigurationCreate | EmailConfigurationUpdate,
    resetForm: () => void
  ) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function EmailConfigurationFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: EmailConfigurationFormProps) {
  const { t } = useTranslation();

  const { control, errors, resetForm, formSubmitHandler, reset, setError, setValue } =
    useEmailConfigurationForm({
      defaultValues,
      onSubmit: (data) => onFormSubmit(data, resetForm),
    });

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  const formFields: FormField<EmailConfigurationCreate | EmailConfigurationUpdate>[] = [
    {
      name: 'email',
      label: t('emailConfiguration.email.label'),
      required: true,
      errorMsg: errors?.email?.message as string | undefined,
      rules: { required: 'Email is required' },
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('emailConfiguration.email.placeholder')}
          type="email"
        />
      ),
    },
    {
      name: 'host',
      label: t('emailConfiguration.host.label'),
      required: true,
      errorMsg: errors?.host?.message as string | undefined,
      rules: { required: 'Host is required' },
      render: (field) => (
        <Input
          {...field}
          disabled={isLoading}
          placeholder={t('emailConfiguration.host.placeholder')}
        />
      ),
    },
    {
      name: 'port',
      label: t('emailConfiguration.port.label'),
      required: true,
      errorMsg: errors?.port?.message as string | undefined,
      rules: { required: 'Port is required' },
      render: (field) => (
        <InputNumber
          {...field}
          disabled={isLoading}
          placeholder={t('emailConfiguration.port.placeholder')}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      name: 'password',
      label: t('emailConfiguration.password.label'),
      required: true,
      errorMsg: errors?.password?.message as string | undefined,
      rules: { required: 'Password is required' },
      render: (field) => (
        <Input.Password
          {...field}
          disabled={isLoading}
          placeholder={t('emailConfiguration.password.placeholder')}
        />
      ),
    },
    {
      name: 'isSslTls',
      label: t('emailConfiguration.isSslTls.label'),
      required: false,
      errorMsg: errors?.isSslTls?.message as string | undefined,
      render: (field) => <Switch {...field} disabled={isLoading} />,
    },
  ];

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <FormBuilder
          formFields={formFields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={columns}
        />
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(
  prevProps: EmailConfigurationFormProps,
  nextProps: EmailConfigurationFormProps
): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(EmailConfigurationFormComponent, areEqual);
